'use client';

import * as React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/custom';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { PasswordRules } from '@/components/auth/PasswordRules';
import { authService } from '@/lib/api/auth';
import { isStrongPassword } from '@/lib/auth/password-rules';

type Field = 'currentPassword' | 'newPassword' | 'confirmPassword';
type FieldErrors = Partial<Record<Field, string>>;

const EMPTY = { currentPassword: '', newPassword: '', confirmPassword: '' };

/** The checks identity-service also makes, run first so a doomed submit never leaves the browser. */
function validate(values: typeof EMPTY): FieldErrors {
  const errors: FieldErrors = {};

  if (!values.currentPassword) errors.currentPassword = 'Enter your current password';
  if (!isStrongPassword(values.newPassword)) {
    errors.newPassword = 'Choose a stronger password — see the requirements below';
  } else if (values.newPassword === values.currentPassword) {
    errors.newPassword = 'New password must be different from your current password';
  }
  if (values.confirmPassword !== values.newPassword) errors.confirmPassword = 'Passwords do not match';
  return errors;
}

/**
 * Change password (FAB-602), opened from the user menu. Asks for the current
 * password, a new one (strength rules shown inline as it is typed) and its
 * confirmation. On success identity-service signs out every other session and
 * keeps this one; on a refusal the error is shown next to the field it is
 * about and nothing is saved.
 */
export function ChangePasswordDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [values, setValues] = React.useState(EMPTY);
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [formError, setFormError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  // Start clean every time it opens, so no typed password outlives the dialog.
  React.useEffect(() => {
    if (open) {
      setValues(EMPTY);
      setErrors({});
      setFormError('');
    }
  }, [open]);

  const set = (field: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setValues((v) => ({ ...v, [field]: value }));
    // An edited field's server error no longer describes what is in it.
    setErrors((errs) => ({ ...errs, [field]: undefined }));
    setFormError('');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    const found = validate(values);

    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    try {
      const res = await authService.changePassword(values);

      toast.success(res?.message || 'Password changed. Your other sessions have been signed out.');
      onOpenChange(false);
    } catch (err: any) {
      const body = err?.response?.data || {};
      const field = body.field as Field | undefined;
      const message = body.message || 'Could not change your password. Please try again.';

      if (field && field in EMPTY) setErrors({ [field]: message });
      else setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  const mismatch = values.confirmPassword !== '' && values.confirmPassword !== values.newPassword;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-[420px]" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
          <DialogDescription>
            Your other signed-in sessions will be signed out. You stay signed in here.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className="space-y-4">
          <PasswordField
            id="current-password"
            label="Current password"
            autoComplete="current-password"
            value={values.currentPassword}
            onChange={set('currentPassword')}
            error={errors.currentPassword}
            disabled={saving}
          />
          <PasswordField
            id="new-password"
            label="New password"
            autoComplete="new-password"
            value={values.newPassword}
            onChange={set('newPassword')}
            error={errors.newPassword}
            disabled={saving}
            describedBy="new-password-rules"
          >
            <PasswordRules id="new-password-rules" value={values.newPassword} />
          </PasswordField>
          <PasswordField
            id="confirm-password"
            label="Confirm new password"
            autoComplete="new-password"
            value={values.confirmPassword}
            onChange={set('confirmPassword')}
            // Mismatch shows as they type; the submit check covers an empty confirm.
            error={errors.confirmPassword || (mismatch ? 'Passwords do not match' : undefined)}
            disabled={saving}
          />

          {formError && (
            <p role="alert" className="text-sm text-cs-error-400" data-testid="change-password-error">
              {formError}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving}>
              {saving ? 'Changing…' : 'Change password'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One labelled password input with its error line. The error is tied to the
 * input through aria-describedby (with any extra hint, e.g. the rules list) so
 * screen readers announce why the field was refused.
 */
function PasswordField({
  id, label, value, onChange, error, disabled, autoComplete, describedBy, children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  disabled?: boolean;
  autoComplete: string;
  describedBy?: string;
  children?: React.ReactNode;
}) {
  const errorId = `${id}-error`;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-cs-text-primary">{label}</label>
      <Input
        id={id}
        type="password"
        value={value}
        onChange={onChange}
        disabled={disabled}
        autoComplete={autoComplete}
        aria-invalid={!!error}
        aria-describedby={[error ? errorId : null, describedBy].filter(Boolean).join(' ') || undefined}
      />
      {error && <p id={errorId} role="alert" className="text-xs text-cs-error-400">{error}</p>}
      {children}
    </div>
  );
}
